import { Category, PolicyDeploymentScope } from "generated/graphql.schema";
import i18next, { LocalKeys } from "utilities/i18n";
import { DesktopDeviceFormSetting } from "routes/Manage/Inventory/Settings-Desktop/DesktopDeviceSettings";
import { DeviceSettingOptions } from "routes/Manage/Inventory/Settings-Desktop/schemas/settingOptions";
import { EnvNames } from "store/env";
import { ImportType } from "routes/Admin/Import/Import";

export const MAX_SEARCH_CHARS = 100;
export const isNew = "https://lens.poly.com/is_new";
export const userMetaKey = "https://lens.poly.com/info";
export const VOID_TID = "all";
export const DESKTOP_PROXY_AGENT = "com.poly.lens.proxy.agent.lens.base";

export const SECOND_IN_MILLISECONDS = 1000;
export const MINUTE_IN_MILLISECONDS = 60 * SECOND_IN_MILLISECONDS;
export const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS;
export const DAY_IN_MILLISECONDS = 24 * HOUR_IN_MILLISECONDS;

export const accountLimit = 20;

export const daysOfTheWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
] as const;

export const getDayOfWeek = (index: number): typeof daysOfTheWeek[number] => {
  return daysOfTheWeek[index];
};

export const DATE_FORMAT = {
  YEAR: "yyyy",
  MONTH: "MMMM",
  MONTH_SHORT: "MMM",
  MONTH_YEAR: "MMMM yyyy",
  MONTH_SHORT_YEAR: "MMM yyyy",
  MONTH_SHORT_DATE_YEAR: "MMM do yyyy",
  DAY: "cccc",
  DAY_INITIAL: "ccccc",
  US_SHORT: "MM/dd/yyyy",
  WITH_TIME_LONG: "PPpp"
};

export const COLORS = {
  CARD_GREY: "#242d37",
  SITE_GREY: "#2b2b2d",
  WHITE: "#ffffff",
  SITE_BLUE: "#3298ff",
  MAP_LAND: "#353332",
  MAP_WATER: "#191a1a",
  THRESHOLD_RED: "#F34C4C"
};

export const DEVICE_FIELD_TO_KEY: Record<string, LocalKeys> = {
  id: "MAC_ADDRESS",
  organization: "ACCOUNT",
  room: "ROOM",
  site: "SITE",
  roomRef: "ROOM",
  siteRef: "SITE",
  hardwareModel: "DEVICE_MODEL",
  softwareVersion: "SOFTWARE_VERSION",
  internalIp: "PRIVATE_IP",
  externalIp: "PUBLIC_IP",
  macAddress: "MAC_ADDRESS",
  serialNumber: "SERIAL_NUMBER"
};

export const MIN_SOFTWARE_FOR_UTOPIA_OPTIONS = "1.5";

// ENV Names Constants
export const PROD: EnvNames = "prod";
export const STAGE: EnvNames = "stage";
export const DEV: EnvNames = "dev";
export const DEVELOPMENT: EnvNames = "Development";

// Lens Help Constants
type HelpURLS =
  | "https://lensuistagepreviewdocs.z19.web.core.windows.net/"
  | "https://info.stage.lens.poly.com/"
  | "https://info.lens.poly.com/";

export const HelpURLSMap: Record<EnvNames, HelpURLS> = {
  Development: "https://lensuistagepreviewdocs.z19.web.core.windows.net/",
  dev: "https://lensuistagepreviewdocs.z19.web.core.windows.net/",
  stage: "https://info.stage.lens.poly.com/",
  prod: "https://info.lens.poly.com/"
};

export const DOCS: Record<string, string> = {
  IMPORT_MIGRATION: "docs/import/import-migration",
  BLOG: "blog",
  NOTIFICATION_OVERVIEW: "docs/notification/nc-overview",
  PIN_ONBOARDING: "docs/onboard/onboarding",
  POLY_PLUS_FEATURES: "docs/licenses/poly-plus-features",
  ASSIGN_DEVICE_TO_SITE: "docs/sites/assign-device-to-site",
  MANAGE_DEVICE_GROUPS: "docs/devicegroups/device-groups-manage",
  MANAGE_DEVICE_USER_GROUPS: "docs/devicegroups/device-groups-manage", //TODO - update to user groups
  LENS_DESKTOP_RELEASE_NOTES: "lens-dt-rn",
  CSV_IMPORT: "docs/policy/csv-import"
};

export const MOBILE_BETA_LINK =
  "https://polycom.centercode.com/key/PolyLensMobile";

export const TOS_LINK =
  "https://www.poly.com/us/en/legal/terms/lens-supplemental-service-terms";

export const PRIVACY_LINK = "https://www.poly.com/us/en/legal/privacy";

export const STATUS_LINK = "https://status.plcm.vc";

export enum SupportLinkType {
  HOME = "HOME",
  DOCS_AND_SOFTWARE = "DOCS_AND_SOFTWARE",
  KNOWLEDGE_BASE = "KNOWLEDGE_BASE",
  DEVICES = "DEVICES",
  LENS = "LENS"
}

export const translationKeys: Record<SupportLinkType, LocalKeys> = {
  [SupportLinkType.HOME]: "SUPPORT_HOME",
  [SupportLinkType.DOCS_AND_SOFTWARE]: "SUPPORT_DOCS_AND_SOFTWARE",
  [SupportLinkType.KNOWLEDGE_BASE]: "SUPPORT_KNOWLEDGE_BASE",
  [SupportLinkType.DEVICES]: "SUPPORT_DEVICES",
  [SupportLinkType.LENS]: "SUPPORT_LENS"
};

export const SUPPORT_LINKS: Record<SupportLinkType, string> = {
  [SupportLinkType.HOME]: "https://support.polycom.com/content/support.html",
  [SupportLinkType.DOCS_AND_SOFTWARE]:
    "https://support.polycom.com/content/support/north-america/usa/en/support.html",
  [SupportLinkType.KNOWLEDGE_BASE]:
    "https://support.polycom.com/PolycomService/coveo/search.htm",
  // NOTE: https version of support portal does not work properly....
  [SupportLinkType.DEVICES]: "http://poly.com/support",
  [SupportLinkType.LENS]: HelpURLSMap.prod
};

export const supportedDevicesPin = [
  "G7500",
  "Studio X30",
  "Studio X50",
  "Studio X70"
];
export const supportedDevicesProv = [
  ...supportedDevicesPin,
  "Trio 8300",
  "Trio 8500",
  "Trio 8800",
  "Trio C60",
  "CCX 400",
  "CCX 500",
  "CCX 600",
  "CCX 700",
  "VVX 101",
  "VVX 150",
  "VVX 201",
  "VVX 250",
  "VVX 301",
  "VVX 311",
  "VVX 350",
  "VVX 401",
  "VVX 411",
  "VVX 450",
  "VVX 501",
  "VVX 601",
  "Studio USB",
  "EagleEye Cube"
];

export const DEVICE_PARAM_TYPE = {
  STRING: "String",
  STRING_PASSWORD: "StringPassword",
  UINT: "UInt",
  UINT_NULL: "UIntNull",
  SINT: "SInt",
  SINT_NULL: "SIntNull",
  ENUM: "Enum",
  BOOL: "Bool",
  XBOOL: "xBool"
};

export const PARAM_TYPE = {
  PARAM: "param",
  PARAM_ARRAY: "paramArray",
  PARAM_2D_ARRAY: "param2DArray"
};

export const LENS_USER_TYPE = {
  SIMPLE_USER: 1
};

export const INHERITANCE_ORDER = {
  [PolicyDeploymentScope.Global]: 1,
  [PolicyDeploymentScope.DeviceModel]: 2,
  [PolicyDeploymentScope.Group]: 3,
  [PolicyDeploymentScope.Site]: 4,
  [PolicyDeploymentScope.Device]: 5,
  [PolicyDeploymentScope.ExternalDevice]: 6
};

export const LOGIN = "login";
export const SIGNUP = "signUp";

export const UNASSIGNED = "unassigned";

export const POLY_LENS_DESKTOP = "Poly Lens Desktop";
export const LENS_DESKTOP = "Lens Desktop";
export const LENS_MOBILE = "Lens Mobile";
export const BT600 = "Plantronics BT600";
export const INITIAL_CLIENT = "initialClient";

export const LENS_DESKTOP_WINDOWS = "lens-desktop-windows";
export const LENS_DESKTOP_MAC = "lens-desktop-mac";
export const OVERRIDE_USER_ID = "user-123";

export const INVALID_CIDR_VALUE = "invalid cidr value";

export const DEVICE_ACTION_TYPES = {
  OFFLINE_DEVICES: "Devices Offline",
  AVAILABLE_UPDATES: "Available Updates",
  SITE_UNASSIGNED: "Unassigned Site"
};

export const NOTIFICATION_ACTION_TYPES: { [index: string]: LocalKeys } = {
  DEVICE_CAN_BE_UPDATED: "SOFTWARE_UPDATES_AVAILABLE",
  DEVICE_OFFLINE: "DEVICES_OFFLINE",
  LICENSES_EXPIRING: "LICENSES_EXPIRING"
};

// CSV & Zip Upload Constants
export enum FILE_EXTENSIONS {
  ZIP = ".zip",
  CSV = ".csv"
}

export const CSV_SUPPORTED_FORMATS = [
  ".csv",
  "text/csv",
  "application/vnd.ms-excel",
  "application/csv",
  "text/x-csv",
  "application/x-csv",
  "text/comma-separated-values",
  "text/x-comma-separated-values"
];
export const ZIP_SUPPORTED_FORMATS = [
  "application/zip",
  "application/x-zip-compressed",
  "application/x-winzip",
  "application/x-zip",
  "multipart/x-zip"
];

export const IMPORT_SUPPORTED_FORMATS = {
  [FILE_EXTENSIONS.ZIP]: ZIP_SUPPORTED_FORMATS,
  [FILE_EXTENSIONS.CSV]: CSV_SUPPORTED_FORMATS
};

export const PFX_SUPPORTED_FORMATS = ["application/x-pkcs12"];

export const CSV_MANDATORY_HEADERS: Record<ImportType, string[]> = {
  MIGRATION: [],
  DEVICE: ["device_model", "mac_address", "serial_number"],
  DEVICE_GROUP: ["group_name", "device_models"],
  SITE: ["site_name"],
  DEVICE_MODEL: ["device_models"]
};
export const CSV_DEVICE_GROUPS_HEADER = "device_groups";
export const CSV_SITE_NAME_HEADER = "site_name";
export const CSV_ROOM_NAME_HEADER = "room_name";
export const CSV_MAX_FILE_SIZE = 3500000;
export const CSV_MAX_FILE_SIZE_MB = CSV_MAX_FILE_SIZE / 1000000;
export const DEVICE_IMPORT_CSV_TEMPLATE_TEXT =
  "mac_address,serial_number,device_model,feature.lens.enabled,lcl.datetime.time.24HourClock,powerSaving.enable,lcl.ml.lang\n64167F8ABCDE,64167F8ABCDE,Trio 8300,1,1,0,English_United_States";
export const DEVICE_GROUP_IMPORT_CSV_TEMPLATE_TEXT =
  "group_name,description,device_models,feature.lens.enabled,lcl.datetime.time.24HourClock,powerSaving.enable,lcl.ml.lang\nMy Group,group description,Trio 8300|Trio 8500,1,1,0,English_United_States";
export const SITE_IMPORT_CSV_TEMPLATE_TEXT =
  'site_name,description,location_name,subnets,device_models,feature.lens.enabled,lcl.datetime.time.24HourClock,powerSaving.enable,lcl.ml.lang\nMy Site,site description,Austin,"{""public-subnet-list"":[{""mask-length"":32,""ip-address"":""86.183.98.84"",""private-subnet-list"":[{""mask-length"":24,""ip-address"":""192.168.1.0""}]}]}",Trio 8300|Trio 8500,1,1,0,English_United_States';
export const DEVICE_MODEL_IMPORT_CSV_TEMPLATE_TEXT =
  "device_models,feature.lens.enabled,lcl.datetime.time.24HourClock,powerSaving.enable,lcl.ml.lang\nTrio 8300|Trio 8500|Trio 8800,1,1,0,English_United_States";
export const DEVICE_EXISTS_SAME_TENANT_RESPONSE_CODE =
  "DEVICE EXISTS ON THIS TENANT";
export const DEVICE_EXISTS_DIFFERENT_TENANT_RESPONSE_CODE =
  "DEVICE EXISTS ON DIFFERENT TENANT";
export const DEVICE_SETTING_INCORRECT = "INCORRECT";
export const DEVICE_EXISTS_SAME_TENANT_TRANSLATE_KEY =
  "IMPORT_ERROR_DEVICE_EXISTS_SAME_TENANT";
export const DEVICE_EXISTS_DIFFERENT_TENANT_TRANSLATE_KEY =
  "IMPORT_ERROR_DEVICE_EXISTS_DIFFERENT_TENANT";
export const FAILED_TO_SET_SITE = "FAILED TO SET SITE";
export const FAILED_TO_SET_SITE_CONFIGURATION =
  "FAILED TO SET SITE CONFIGURATION";
export const NO_MODELS_PROVIDED = "no models provided";
export const FAILED_TO_SET_SITE_TOPOLOGY = "FAILED TO SET SITE TOPOLOGY";
export const DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_TRANSLATE_KEY =
  "IMPORT_ERROR_DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE";
export const DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_RESPONSE_CODE =
  "DEVICE COULD NOT BE ASSIGNED TO SITE";
// Software Update Constants
export const SOFTWARE_UPDATE = "Software Update";
export const LATEST = "LATEST";
export const AUTO_UPDATE_LATEST = "autoUpdateLatest";
export const UPDATE_SOURCE = "swUpdateSource";
export const UPDATE_PATH = "swUpdatePath";
export const SUPPORT_UPDATE_PATH = "swSupportUpdatePath";
export const CUSTOM_UPDATE_PATH = "customSwUpdatePath";
export const CUSTOM_SUPPORT_UPDATE_PATH = "customSwSupportUpdatePath";
export const SCHEDULE_UPDATE_CHECKS = "upgradeTimeFrameEnable";
export const UPDATE_START_TIME = "upgradeTimeFrameStart";
export const UPDATE_STOP_TIME = "upgradeTimeFrameStop";
export const POLLING_INTERVAL = "upgradePollingInterval";

// UCS Devices with Screens Constants
// IMPORTANT: this SHOULD be a list of UCS devices with a screen.
// IF this list gets changed to NOT be UCS devices with a screen,
// Then the Screen Capture router conditions need to be updated / addressed
export const UCS_DEVICES_WITH_SCREEN = [
  "VVX",
  "CCX",
  "Trio",
  "RealPresence Trio",
  "VVX Family",
  "Trio Family",
  "CCX Family",
  "Edge"
];

// Devices that support additional SW Update paths
export const DEVICES_WITH_ADDITIONAL_SW_UPDATE_PATHS = [
  {
    models: ["Rove B2", "Rove B4"],
    additionalUpdatePathsToDisplay: [
      "Rove B2",
      "Rove B4",
      "Rove 30",
      "Rove 40",
      "Rove R8"
    ]
  }
];

export const DIAGNOSTICS_SUPPORT = UCS_DEVICES_WITH_SCREEN;
export const MIN_SOFTWARE_FOR_POLYOS_LOG_DOWNLOAD = "3.4";
export const MIN_SOFTWARE_FOR_POLYOS_LOG_FLASH = "3.5";
export const MIN_SOFTWARE_FOR_EDGE_LOG_DOWNLOAD = "1.0.1.5589";
export const MIN_SOFTWARE_FOR_DEVICE_CONFIG = "5.9.8";

// My Settings Views
export const MY_SETTINGS_POLICY_VIEW = "policy";
export const MY_SETTINGS_CHANGES_VIEW = "changes";
export const MY_SETTINGS_DEVICE_CONFIG_VIEW = "device-config";

// Desktop Device Form Settings
export type DesktopDeviceFormSettings = {
  [key: string]: DesktopDeviceFormSetting[];
};

export const DESKTOP_DEVICE_FORM_SETTINGS: DesktopDeviceFormSettings = {
  LANGUAGE: [
    {
      tooltipText: i18next.t("LANGUAGE_DESC"),
      options: DeviceSettingOptions.languageOpts,
      accessor: "general.language",
      type: "dropdown"
    }
  ],
  GENERAL: [
    {
      tooltipText: i18next.t("PARTICIPANT_COUNT_DESC"),
      options: [],
      accessor: "video.participant_count",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("ANTI_FLICKER_DESC"),
      options: DeviceSettingOptions.antiFlickerOpts,
      accessor: "video.anti_flicker",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("ANSWER_VOICE_PROMPT_DESC"),
      options: [],
      accessor: "call_control.answer_voice_prompt",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("ANSWERING_CALL_ALERT_DESC"),
      options: [],
      accessor: "call_control.answering_call_alert",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUDIO_CHANNEL_TONE_DESC"),
      options: [],
      accessor: "call_control.audio_channel_tone",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUDIO_SENSING_DESC"),
      options: [],
      accessor: "call_control.audio_sensing",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("CLOSE_CONVERSATION_LIMITING_DESC"),
      options: DeviceSettingOptions.closeConversationLimitingOpts,
      accessor: "call_control.close_conversation_limiting",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("CALLER_ID_DESC"),
      options: [],
      accessor: "call_control.caller_id",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("MUTE_OFF_ALERT_DESC"),
      options: DeviceSettingOptions.muteOffAlertOpts,
      accessor: "call_control.mute_off_alert",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MUTE_REMINDER_MODE_DESC"),
      options: DeviceSettingOptions.muteReminderModeOpts,
      accessor: "call_control.mute_reminder_mode",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MUTE_REMINDER_TIMING_DESC"),
      options: DeviceSettingOptions.muteReminderTimingOpts,
      accessor: "call_control.mute_reminder_timing",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("ONLINE_INDICATOR_DESC"),
      options: [],
      accessor: "call_control.online_indicator",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUTO_ANSWER_CRADLE_DESC"),
      options: [],
      accessor: "call_control.auto_answer_cradle",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("DEFAULT_PHONE_LINE_DESC"),
      options: DeviceSettingOptions.defaultPhoneLineOpts,
      accessor: "call_control.default_phone_line",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("SECOND_INCOMING_CALL_DESC"),
      options: DeviceSettingOptions.secondIncomingCallOpts,
      accessor: "call_control.second_incoming_call",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("INCREASE_QD_HEADSET_VOLUME_DESC"),
      options: DeviceSettingOptions.increaseQdHeadsetVolumeOpts,
      accessor: "call_control.increase_qd_headset_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("TONE_CONTROL_DESC"),
      options: DeviceSettingOptions.toneControlOpts,
      accessor: "audio.tone_control",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MUTE_ALERTS_DESC"),
      options: [],
      accessor: "call_control.mute_alerts",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("MUTE_ALERT_TYPES_DESC"),
      options: DeviceSettingOptions.muteAlertTypeOpts,
      accessor: "call_control.mute_alert_type",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("NOTIFICATION_TONES_DESC"),
      options: [],
      accessor: "call_control.notification_tones",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("CALL_ANNOUNCEMENT_DESC"),
      options: [],
      accessor: "call_control.call_announcement",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("CONNECTION_INDICATION_DESC"),
      options: DeviceSettingOptions.connectionIndicationOpts,
      accessor: "wireless.connection_indication",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("ROCKET_BUTTON_DESC"),
      options: DeviceSettingOptions.rocketButtonOpts,
      accessor: "call_control.rocket_button",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("BATTERY_STATUS_DESC"),
      options: [],
      accessor: "general.battery_status_alert",
      type: "checkbox"
    }
  ],
  RINGTONES_AND_VOLUME: [
    {
      tooltipText: i18next.t("DESK_PHONE_RINGTONE_DESC"),
      options: DeviceSettingOptions.deskPhoneRingtoneOpts,
      accessor: "call_control.desk_phone_ringtone",
      type: "dropdown"
    },
    {
      tooltipText: "",
      options: DeviceSettingOptions.deskPhoneVolumeOpts,
      accessor: "call_control.desk_phone_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MOBILE_PHONE_RINGTONE_DESC"),
      options: DeviceSettingOptions.mobilePhoneRingtoneOpts,
      accessor: "call_control.mobile_phone_ringtone",
      type: "dropdown"
    },
    {
      tooltipText: "",
      options: DeviceSettingOptions.mobilePhoneVolumeOpts,
      accessor: "call_control.mobile_phone_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("COMPUTER_RINGTONE_DESC"),
      options: DeviceSettingOptions.computerRingtoneOpts,
      accessor: "call_control.computer_ringtone",
      type: "dropdown"
    },
    {
      tooltipText: "",
      options: DeviceSettingOptions.computerVolumeOpts,
      accessor: "call_control.computer_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("CHARGE_VIBRATION_DESC"),
      options: [],
      accessor: "general.charge_vibration",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("SIDETONE_DESC"),
      options: DeviceSettingOptions.sidetoneOpts,
      accessor: "call_control.sidetone",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("RING_VIBRATION_DESC"),
      options: [],
      accessor: "call_control.ring_vibration",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("VOLUME_LEVEL_TONES_DESC"),
      options: DeviceSettingOptions.volumeLevelIndicatorsOpts,
      accessor: "audio.volume_level_indicators",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("INDEPENDENT_VOLUME_CONTROL_DESC"),
      options: [],
      accessor: "audio.independent_volume_control",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("ANC_TIMEOUT_DESC"),
      options: DeviceSettingOptions.ancTimeoutOpts,
      accessor: "audio.anc_timeout",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MUTE_REMINDER_VOLUME_DESC"),
      options: DeviceSettingOptions.muteReminderVolumeOpts,
      accessor: "call_control.mute_reminder_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("AUDIO_PROMPT_VOLUME_DESC"),
      options: DeviceSettingOptions.audioPromptVolumeOpts,
      accessor: "call_control.audio_prompt_volume",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("WEARING_PREFERENCE_DESC"),
      options: DeviceSettingOptions.wearingPreferenceOpts,
      accessor: "call_control.wearing_preference",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("RINGTONE_DESC"),
      options: [],
      accessor: "call_control.default_ringtone",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("VOLUME_ALERTS_DESC"),
      options: DeviceSettingOptions.volumeAlertsOpts,
      accessor: "audio.volume_alerts",
      type: "dropdown"
    }
  ],
  AUDIO: [
    {
      tooltipText: i18next.t("POLY_ACOUSTIC_FENCE_DESC"),
      options: DeviceSettingOptions.acousticFenceOpts,
      accessor: "audio.acoustic_fence",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("NOISE_BLOCK_AI_DESC"),
      options: [],
      accessor: "audio.noise_block_ai",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("STEREO_AUDIO_DESC"),
      options: [],
      accessor: "audio.stereo",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUDIO_BASS_DESC"),
      options: [],
      accessor: "audio.bass",
      type: "slider"
    },
    {
      tooltipText: i18next.t("AUDIO_TREBLE_DESC"),
      options: [],
      accessor: "audio.treble",
      type: "slider"
    }
  ],
  SENSORS_AND_PRESENCE: [
    {
      tooltipText: i18next.t("SENSOR_SETTINGS_DESC"),
      options: [],
      accessor: "general.sensor_settings_enabled",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("ACTIVE_CALL_AUDIO_DESC"),
      options: DeviceSettingOptions.activeCallAudioOpts,
      accessor: "call_control.active_call_audio",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("AUTO_ANSWER_DESC"),
      options: [],
      accessor: "call_control.auto_answer",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUTO_PAUSE_MUSIC_DESC"),
      options: [],
      accessor: "audio.auto_pause_music",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("CALL_BUTTON_LOCK_DESC"),
      options: [],
      accessor: "call_control.call_button_lock",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("SMART_AUDIO_TRANSFER_DESC"),
      options: [],
      accessor: "call_control.smart_audio_transfer",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUTO_MUTE_DESC"),
      options: [],
      accessor: "call_control.auto_mute",
      type: "checkbox"
    }
  ],
  SOFTPHONES_AND_MEDIA_PLAYERS: [
    {
      tooltipText: i18next.t("DIALTONE_DESC"),
      options: [],
      accessor: "call_control.dialtone",
      type: "checkbox"
    }
  ],
  WIRELESS: [
    {
      tooltipText: i18next.t("AUDIO_QUALITY_DESC"),
      options: DeviceSettingOptions.audioQualityOpts,
      accessor: "audio.audio_quality",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("EXTENDED_RANGE_DESC"),
      options: [],
      accessor: "audio.extended_range",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("HD_VOICE_DESC"),
      options: DeviceSettingOptions.hdVoiceOpts,
      accessor: "call_control.hd_voice",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("AUTO_CONNECT_MOBILE_DESC"),
      options: [],
      accessor: "call_control.auto_connect_mobile",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("BLUETOOTH_DESC"),
      options: [],
      accessor: "wireless.bluetooth",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("BLUETOOTH_STREAMING_DESC"),
      options: DeviceSettingOptions.bluetoothStreamingOpts,
      accessor: "wireless.bluetooth_streaming",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("DEVICE_AUDIO_BANDWIDTH_DESC", {
        device: "desk phone"
      }),
      options: DeviceSettingOptions.deskPhoneAudioBandwidthOpts,
      accessor: "call_control.desk_phone_audio_bandwidth",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("DEVICE_AUDIO_BANDWIDTH_DESC", {
        device: "computer"
      }),
      options: DeviceSettingOptions.computerAudioBandwidthOpts,
      accessor: "call_control.computer_audio_bandwidth",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MOBILE_VOICE_COMMANDS_DESC"),
      options: [],
      accessor: "call_control.mobile_voice_commands",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("OVER_AIR_SUBSCRIPTION_DESC"),
      options: [],
      accessor: "call_control.over_air_subscription",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("RANGE_DESC"),
      options: [],
      accessor: "call_control.range",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("STREAMING_AUDIO_DESC"),
      options: [],
      accessor: "audio.streaming_audio",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("EXCLUSIVE_CONNECTION_DESC"),
      options: [],
      accessor: "wireless.exclusive_connection",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("SECURE_BLUETOOTH_DESC"),
      options: [],
      accessor: "wireless.secure_bluetooth",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("AUTO_DISCONNECT_CRADLE_DESC"),
      options: [],
      accessor: "call_control.auto_disconnect_cradle",
      type: "checkbox"
    }
  ],
  ADVANCED: [
    {
      tooltipText: i18next.t("G616_DESC"),
      options: [],
      accessor: "audio.g616",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("ANTI_STARTLE_DESC"),
      options: [],
      accessor: "audio.anti_startle",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("NOISE_EXPOSURE_LIMIT_DESC"),
      options: DeviceSettingOptions.noiseExposureLimitOpts,
      accessor: "audio.noise_exposure_limit",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("HOURS_ON_PHONE_DAY_DESC"),
      options: DeviceSettingOptions.hoursOnPhoneDayOpts,
      accessor: "audio.hours_on_phone_per_day",
      type: "dropdown"
    }
  ],
  VIDEO_CONTROLS: [
    {
      tooltipText: i18next.t("BRIGHTNESS_DESC"),
      options: [],
      accessor: "video.brightness",
      type: "slider"
    },
    {
      tooltipText: i18next.t("CONTRAST_DESC"),
      options: [],
      accessor: "video.contrast",
      type: "slider"
    },
    {
      tooltipText: i18next.t("HUE_DESC"),
      options: [],
      accessor: "video.hue",
      type: "slider"
    },
    {
      tooltipText: i18next.t("SATURATION_DESC"),
      options: [],
      accessor: "video.saturation",
      type: "slider"
    },
    {
      tooltipText: i18next.t("SHARPNESS_DESC"),
      options: [],
      accessor: "video.sharpness",
      type: "slider"
    },
    {
      tooltipText: i18next.t("GAMMA_DESC"),
      options: [],
      accessor: "video.gamma",
      type: "slider"
    },
    {
      tooltipText: i18next.t("WHITE_BALANCE_DESC"),
      options: [],
      accessor: "video.white_balance",
      type: "slider"
    },
    {
      tooltipText: i18next.t("BACK_LIGHT_COMPENSATION_DESC"),
      options: [],
      accessor: "video.back_light_compensation",
      type: "slider"
    },
    {
      tooltipText: i18next.t("GAIN_DESC"),
      options: [],
      accessor: "video.gain",
      type: "slider"
    },
    {
      tooltipText: i18next.t("FOCUS_DESC"),
      options: [],
      accessor: "video.focus",
      type: "slider"
    },
    {
      tooltipText: i18next.t("EXPOSURE_DESC"),
      options: [],
      accessor: "video.exposure",
      type: "slider"
    },
    {
      tooltipText: i18next.t("WIDE_DYNAMIC_RANGE_DESC"),
      options: [],
      accessor: "video.wide_dynamic_range",
      type: "checkbox"
    },
    {
      tooltipText: i18next.t("TRACKING_MODE_DESC"),
      options: DeviceSettingOptions.trackingModeOpts,
      accessor: "video.tracking_mode",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("MAX_ZOOM_DESC"),
      options: DeviceSettingOptions.maxZoomOpts,
      accessor: "video.max_zoom",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("CAMERA_MOVEMENT_DESC"),
      options: DeviceSettingOptions.cameraMovementOpts,
      accessor: "video.camera_movement",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("TRACKING_SPEED_DESC"),
      options: DeviceSettingOptions.trackingSpeedOpts,
      accessor: "video.tracking_speed",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("FRAME_SIZE_DESC"),
      options: DeviceSettingOptions.frameSizeOpts,
      accessor: "video.frame_size",
      type: "dropdown"
    },
    {
      tooltipText: i18next.t("ZOOM_DESC"),
      options: [],
      accessor: "video.zoom",
      type: "slider"
    },
    {
      tooltipText: i18next.t("SKIN_ENHANCEMENT_DESC"),
      options: [],
      accessor: "video.skin_enhancement",
      type: "checkbox"
    }
  ],
  DIAGNOSTICS: [
    {
      tooltipText: i18next.t("ONSCREEN_DISPLAY_DESC"),
      options: [],
      accessor: "video.onscreen_display",
      type: "checkbox"
    }
  ],
  RESET_DEVICE: [
    {
      tooltipText: "",
      options: [],
      accessor: "general.restore_defaults",
      type: "button"
    }
  ],
  SOFTWARE_UPDATE: [
    {
      tooltipText: "",
      options: [],
      accessor: "software_update.policy.use_latest",
      type: "checkbox"
    },
    {
      tooltipText: "",
      options: [], // will be filled out by incoming constraints
      accessor: "software_update.policy.version",
      type: "dropdown"
    }
  ]
};

// Desktop Device Form Setting Dependencies
export type DesktopSettingDependency = {
  accessor: string;
  action: "hide" | "disable";
  when: DependencySource[];
};

export type DependencySource = {
  accessor: string;
  value: string | boolean;
};

export const DESKTOP_DEVICE_SETTING_DEPENDENCIES: DesktopSettingDependency[] = [
  {
    accessor: "call_control.auto_answer",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "call_control.smart_audio_transfer",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "call_control.call_button_lock",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "audio.auto_pause_music",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "call_control.auto_mute",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "call_control.active_call_audio",
    action: "disable",
    when: [
      {
        accessor: "general.sensor_settings_enabled",
        value: false
      }
    ]
  },
  {
    accessor: "audio.hours_on_phone_per_day",
    action: "hide",
    when: [
      {
        accessor: "audio.noise_exposure_limit",
        value: "off"
      }
    ]
  },
  {
    accessor: "call_control.mute_reminder_timing",
    action: "hide",
    when: [
      {
        accessor: "call_control.mute_reminder_mode",
        value: "off"
      }
    ]
  },
  {
    accessor: "call_control.mute_reminder_timing",
    action: "disable",
    when: [
      {
        accessor: "call_control.mute_reminder_mode",
        value: "voiceAudible"
      },
      {
        accessor: "call_control.mute_reminder_mode",
        value: "voiceVisible"
      },
      {
        accessor: "call_control.mute_reminder_mode",
        value: "voiceVisibleAndAudible"
      }
    ]
  },
  {
    accessor: "call_control.mute_alert_type",
    action: "disable",
    when: [
      {
        accessor: "call_control.mute_alerts",
        value: false
      }
    ]
  },
  {
    accessor: "video.exposure",
    action: "disable",
    when: [
      {
        accessor: "video.exposure_auto_enabled",
        value: true
      }
    ]
  },
  {
    accessor: "video.white_balance",
    action: "disable",
    when: [
      {
        accessor: "video.white_balance_auto_enabled",
        value: true
      }
    ]
  },
  {
    accessor: "video.focus",
    action: "disable",
    when: [
      {
        accessor: "video.focus_auto_enabled",
        value: true
      }
    ]
  },
  {
    accessor: "software_update.policy.version",
    action: "hide",
    when: [
      {
        accessor: "software_update.policy.use_latest",
        value: true
      }
    ]
  }
];

export const STAGED_LAST_CONFIG_REQUEST_TIME_MILLISECONDS = 10;

// Provisioned Settings Constants
export const DEFAULT_CATEGORY = "General";
export const DEFAULT_SUBCATEGORY = "Other";
export const DEFAULT_PARAM_ARRAY_COUNT = 1;
export const NO_GROUPING = "no_grouping";
export const MAIN = "Main";
export const LINE = "Line";
export const LINE_LIMIT_PARAM = "reg.limit";
export const PARAM_ARRAY_PLACEHOLDER = "NUM_REPLACE_";
export const CALL_CONFIGURATION = "Call Configuration";
export const GLOBAL_SETTINGS = "Global Settings";
export const LINES = "Lines";
export const SERVER_SETTINGS_NAME = "reg.NUM_REPLACE_1.server";
export const SERVER = "Server";
export const INTEGRATIONS = "Integrations";
export const TEAMS = "Microsoft Teams";

export const PRODUCT_IDS = {
  // aka "Lens Control Service" - the Lens app running on a Microsoft Teams Room instance
  MTR: "mtr"
};

export const MODEL_IDS = {
  // aka "Lens Control Service" - the Lens app running on a Microsoft Teams Room instance
  MTR: "099331ed-293e-44a1-b9f1-21b78d298e6d"
};

export const FAMILY_IDS = {
  Clariti: "99192d2a-5006-4199-bef6-ddfaff7136e4",
  // currently only MTR room PCs
  RoomPC: "547d9157-2c2f-40ae-ac6d-cc53389afea0"
};

export const NBSP = "\u00A0";

// Clariti Trial Settings
export const CLARITI_TRIAL_WELCOME_VIDEO_ID = "75GABnONZmg";
export const CLARITI_TRIAL_TELL_ME_MORE_URL =
  "https://www.poly.com/us/en/products/video-conferencing/clariti";

/**
 *  Insights\Analysis Constants
 */

export enum TopicGroup {
  DEVICE_ADOPTION = "DEVICE_ADOPTION",
  DEVICE_HEALTH = "DEVICE_HEALTH",
  ROOM_UTILIZATION = "ROOM_UTILIZATION",
  MEETING_BEHAVIORS = "MEETING_BEHAVIORS"
}

export enum Topic {
  CALL_USAGE = "CALL_USAGE",
  DEVICES_USED = "DEVICES_USED",
  DEVICES_UNUSED = "DEVICES_UNUSED",
  DEVICES_OFFLINE = "DEVICES_OFFLINE",
  DEVICES_OUT_OF_SERVICE = "DEVICES_OUT_OF_SERVICE",
  MEETING_SIZE = "MEETING_SIZE",
  ROOM_USAGE = "ROOM_USAGE",
  MEETINGS_EMPTY = "MEETINGS_EMPTY",
  MEETINGS_SERIES_EMPTY = "MEETINGS_SERIES_EMPTY",
  ROOM_USAGE_UNSCHEDULED = "ROOM_USAGE_UNSCHEDULED",
  MEETINGS_LONG = "MEETINGS_LONG",
  MEETINGS_LATE = "MEETINGS_LATE"
}

export const topicGroupsByCategory: Record<Category, TopicGroup[]> = {
  [Category.Device]: [TopicGroup.DEVICE_ADOPTION, TopicGroup.DEVICE_HEALTH],
  [Category.Room]: [TopicGroup.ROOM_UTILIZATION, TopicGroup.MEETING_BEHAVIORS]
};

export const topicsByGroup: Record<TopicGroup, Topic[]> = {
  [TopicGroup.DEVICE_ADOPTION]: [
    Topic.CALL_USAGE,
    Topic.DEVICES_USED,
    Topic.DEVICES_UNUSED
  ],
  [TopicGroup.DEVICE_HEALTH]: [
    Topic.DEVICES_OFFLINE,
    Topic.DEVICES_OUT_OF_SERVICE
  ],
  [TopicGroup.ROOM_UTILIZATION]: [Topic.MEETING_SIZE, Topic.ROOM_USAGE],
  [TopicGroup.MEETING_BEHAVIORS]: [
    Topic.MEETINGS_EMPTY,
    Topic.MEETINGS_SERIES_EMPTY,
    Topic.ROOM_USAGE_UNSCHEDULED,
    Topic.MEETINGS_LONG,
    Topic.MEETINGS_LATE
  ]
};

export const topicIds: Record<Topic, string> = {
  [Topic.CALL_USAGE]: "Call Usage",
  [Topic.DEVICES_USED]: "Devices used",
  [Topic.DEVICES_UNUSED]: "Devices unused",
  [Topic.DEVICES_OFFLINE]: "Offline devices",
  [Topic.DEVICES_OUT_OF_SERVICE]: "Devices out of service",
  [Topic.MEETING_SIZE]: "Average meeting size",
  [Topic.ROOM_USAGE]: "Room usage",
  [Topic.MEETINGS_EMPTY]: "Rooms scheduled but not used",
  [Topic.MEETINGS_SERIES_EMPTY]: "Recurring meetings with no participants",
  [Topic.ROOM_USAGE_UNSCHEDULED]: "Unscheduled room usage",
  [Topic.MEETINGS_LONG]: "Meetings that run long",
  [Topic.MEETINGS_LATE]: "Meetings that start late"
};

export const topicKeys: Record<string, Topic> = Object.fromEntries(
  Object.entries(topicIds).map(([k, v]) => [v, k as Topic])
);
